import { TitleButtonDivider } from '@/common/buttons/TitleButtonDivider';
import { EntityPerspective } from '@/common/entityFramework/entityPerspective/EntityPerspective';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { StringUtils } from '@/common/utils/StringUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { TheraSelect } from '../../theraSelect/TheraSelect';

export const AbvGtnSalesPerspective = () => {
  const entityPerspectiveRef = useRef();
  const theraSelectRef = useRef();
  const module = useModuleFromRoute();

  const rsrc = useRsrc();
  const [channelTrend, setChannelTrend] = useState(null);

  const channelTrendGroups = Array.isArray(channelTrend) ? channelTrend : [];

  const channelTrendValues = channelTrendGroups.map((group) => ({
    key: StringUtils.toUpperCamel(group.key),
    label: group.label,
  }));

  const views = [
    {
      name: 'Channel Trend',
      dims: ['PAID_PERIOD', 'COMPANY_CODE', 'PROD', 'CHANNEL'],
      kpis: ['GROSS_SALES', 'DISC', 'NET_SALES'],
      trends: [
        {
          kpis: ['DISC'],
          dims: ['CHANNEL'],
          layout: 'KPI_OVER_DIM',
          values: { CHANNEL: channelTrendValues },
        },
      ],
      trendValueGroups: { CHANNEL: channelTrendGroups },
      dimToPeriodicities: {
        PAID_PERIOD: ['YEAR', 'QTR', 'MON'],
      },
    },
    {
      name: 'Sales',
      dims: ['PAID_PERIOD', 'COMPANY_CODE', 'PROD', 'CHANNEL'],
      kpis: ['GROSS_SALES', 'DISC', 'NET_SALES'],
      dimToPeriodicities: {
        PAID_PERIOD: ['YEAR', 'QTR', 'MON'],
      },
    },
  ];

  useEffect(() => {
    const toChannelTrendGroups = (channelIdMap) => {
      if (Array.isArray(channelIdMap)) return channelIdMap;
      if (!channelIdMap || typeof channelIdMap !== 'object') return [];

      return Object.entries(channelIdMap)
        .filter(([, ids]) => Array.isArray(ids) && ids.length > 0)
        .map(([groupLabel, ids]) => ({ key: groupLabel, label: groupLabel, ids }));
    };

    rsrc
      .data(module, 'GTN_SALES_TREND_CHANNEL_DATA_PLUGIN', { entity: 'GTN_SALES' }, 'GTN_SALES')
      .then((res) => setChannelTrend(toChannelTrendGroups(res)));
  }, []);

  const getContextMenuItems = (params) => {
    let contextMenus = entityPerspectiveRef.current.getContextMenuItems(params);

    let discSubMenu = [
      {
        name: 'Discounts Transactions',
        action: () => entityPerspectiveRef.current.navigateToViewer(params, 'DISC', 'Discounts Transactions'),
        icon: fas.faListUl,
      },
      {
        name: 'Pivot Discounts',
        action: () => entityPerspectiveRef.current.navigateToPivot(params, 'DISC', 'Pivot Discounts'),
        icon: fas.faTableColumns,
      },
    ];

    let grossSalesSubMenu = [
      {
        name: 'Gross Sales Transactions',
        action: () => entityPerspectiveRef.current.navigateToViewer(params, 'GROSS_SALES', 'Gross Sales Transactions'),
        icon: fas.faListUl,
      },
      {
        name: 'Pivot Gross Sales Transactions',
        action: () =>
          entityPerspectiveRef.current.navigateToPivot(params, 'GROSS_SALES', 'Pivot Gross Sales Transactions'),
        icon: fas.faTableColumns,
      },
    ];

    contextMenus.push(
      {
        name: 'Gross Sales',
        subMenu: grossSalesSubMenu,
        icon: fas.faListUl,
      },
      {
        name: 'Discounts',
        subMenu: discSubMenu,
        icon: fas.faListUl,
      },
      {
        name: 'Export',
        action: () => entityPerspectiveRef.current.openExportDialog(params),
        icon: fas.faArrowCircleDown,
      },
    );

    return contextMenus;
  };

  const handleViewReset = useCallback(() => {
    theraSelectRef.current.reset();
  }, []);

  const events = {
    onDimFilterChange: ({ dimCode }) => {
      if (dimCode === 'PROD') {
        theraSelectRef.current.reset();
      }
    },
  };
  const augButtons = (buttons) => {
    buttons.push(
      <TheraSelect entityPerspectiveRef={entityPerspectiveRef} ref={theraSelectRef} />,
      <TitleButtonDivider key="filterButtonDivider" />,
    );
  };

  if (channelTrend === null) return null;
  return (
    <EntityPerspective
      ref={entityPerspectiveRef}
      views={views}
      augButtons={augButtons}
      onViewReset={handleViewReset}
      events={events}
      gridOptions={{
        getContextMenuItems,
      }}
    />
  );
};
